// Formula Analyzer modal — paste (or auto-prefill) a formula, call the
// backend explainer, then paint a step-by-step explanation sheet.
// Isolated feature file: safe to delete for a full revert.
// Overlay/dialog styling mirrors SignOutDialog in MainLayout.tsx.

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/services/api';
import { explainFormula } from '@/services/formulaAnalyzer';
import { writeFormulaExplanation } from '@/services/formulaSheet';
import { isInOffice } from '@/services/office';

interface FormulaAnalyzerModalProps {
  open: boolean;
  onClose: () => void;
}

// Matches the backend DTO cap (formula binding max=2000).
const MAX_FORMULA_LEN = 2000;

export function FormulaAnalyzerModal({ open, onClose }: FormulaAnalyzerModalProps) {
  const [formula, setFormula] = useState('');
  const [loading, setLoading] = useState(false);

  // Best-effort prefill from the ACTIVE cell's formula on open. Only
  // fills an empty textarea (never clobbers user input) and swallows
  // every failure silently — prefill is a nicety, not a requirement.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        if (!isInOffice() || typeof Excel === 'undefined') return;
        const active = await Excel.run(async (ctx) => {
          const cell = ctx.workbook.getActiveCell();
          cell.load('formulas');
          await ctx.sync();
          const f = cell.formulas?.[0]?.[0];
          return typeof f === 'string' ? f : '';
        });
        if (!cancelled && active.startsWith('=')) {
          setFormula((prev) => (prev.trim() === '' ? active : prev));
        }
      } catch {
        // Silent — cell-edit mode, chart selected, old host, etc.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Escape closes (when not mid-request).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const submit = async () => {
    const text = formula.trim();
    if (!text || loading) return;
    // Client-side validation BEFORE the API call — saves the user's
    // credits on inputs the backend/AI can't meaningfully explain.
    if (text.length > MAX_FORMULA_LEN) {
      toast.error(`Formula is too long (max ${MAX_FORMULA_LEN} characters).`);
      return;
    }
    if (!text.startsWith('=')) {
      toast.error('That doesn’t look like a formula — it should start with "=".');
      return;
    }
    // Multiple formulas pasted (e.g. two lines each starting with "=") —
    // the analyzer explains ONE formula at a time.
    const eqStarts = text.split('\n').filter((l) => l.trim().startsWith('=')).length;
    if (eqStarts > 1) {
      toast.error('Please paste ONE formula at a time.');
      return;
    }
    setLoading(true);
    try {
      const res = await explainFormula(text);
      await writeFormulaExplanation(res.explanation);
      const credits =
        typeof res.credits_remaining === 'number' && res.credits_remaining >= 0
          ? ` · ${res.credits_remaining} credits left`
          : '';
      toast.success(`Explanation sheet created${credits}`);
      setFormula('');
      onClose();
    } catch (err) {
      // Keep the modal open so the user can retry / fix the formula.
      toast.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="formula-analyzer-title"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-xl border border-border bg-white shadow-2xl dark:border-border-dark dark:bg-surface-dark"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-5 py-4 dark:border-border-dark">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 font-serif text-base font-bold italic text-primary">
            fx
          </span>
          <div className="min-w-0">
            <h2
              id="formula-analyzer-title"
              className="text-base font-semibold text-text-primary dark:text-text-primary-dark"
            >
              Formula Analyzer
            </h2>
            <p className="truncate text-xs text-text-secondary">
              Step-by-step explanation, on a new sheet
            </p>
          </div>
        </div>
        <div className="px-5 py-4">
          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
            Formula
          </label>
          <textarea
            value={formula}
            onChange={(e) => setFormula(e.target.value)}
            rows={3}
            disabled={loading}
            maxLength={MAX_FORMULA_LEN}
            placeholder="=VLOOKUP(A2, Sheet2!A:D, 3, FALSE)"
            spellCheck={false}
            autoFocus
            className="w-full resize-none rounded-lg border border-border bg-white px-3 py-2.5 font-mono text-sm leading-relaxed text-text-primary placeholder:text-text-secondary/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60 dark:border-border-dark dark:bg-surface-dark dark:text-text-primary-dark"
          />
          <div className="mt-2 flex items-start justify-between gap-2">
            <p className="text-[11px] leading-relaxed text-text-secondary">
              Tip: select a formula cell before opening — it fills in automatically.
            </p>
            {formula.length > MAX_FORMULA_LEN * 0.75 && (
              <span className="shrink-0 text-[11px] font-medium text-text-secondary">
                {formula.length}/{MAX_FORMULA_LEN}
              </span>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-black/[0.02] px-5 py-3 dark:border-border-dark dark:bg-white/[0.02]">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-border px-3.5 py-1.5 text-sm font-medium hover:bg-black/5 disabled:opacity-50 dark:border-border-dark dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading || formula.trim() === ''}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50"
          >
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {loading ? 'Creating sheet…' : 'Explain'}
          </button>
        </div>
      </div>
    </div>
  );
}
